import * as React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux";
import { dataSourceItem } from "../../redux/models/reduxModels";
import { peekClientType, resolveClientType } from "../../services/clientTypeService";
import { ClientTypeContext, hasRestrictionEntries, INERT_CLIENT_TYPES, toClientTypeContext } from "../helpers/dataSourceFieldHelper";

/** The whole feature is opt-in: without a client list AND at least one datasource that actually
 * carries a restriction, no request is ever issued and the dimension stays inert. A tenant that
 * does not use client types therefore executes exactly the code paths it executes today.
 * Same emptiness rule as the evaluator (hasRestrictionEntries): a restriction that is all blank
 * entries (";") restricts nothing, so it must not switch the feature on either. */
export const clientTypeRestrictionsInUse = (clientListUrl?: string, datasources?: dataSourceItem[]): boolean =>
  !!clientListUrl?.trim()
  && Array.isArray(datasources)
  && datasources.some(item => hasRestrictionEntries(item?.clientType) || hasRestrictionEntries(item?.clientTypeBlacklist));

/**
 * The client-type context for a single site. Reads the session cache synchronously so a render
 * path never awaits; on a miss it returns inert, starts the lookup and re-renders when it
 * lands. Callers should prefetch on location change so that miss path stays off screen.
 *
 * NOTE for consumers: the re-render alone does NOT fix a value captured in a one-shot
 * `useState` initializer - resolve datasources in a `useMemo` keyed on this hook's return
 * value instead.
 */
export const useClientTypeContext = (compositeSiteId?: string): ClientTypeContext => {
  const clientListUrl: string = useSelector((state: RootState) => state.tenantInfo?.clientListUrl);
  const datasources: dataSourceItem[] = useSelector((state: RootState) => state.tenantInfo?.datasources);

  const inUse: boolean = clientTypeRestrictionsInUse(clientListUrl, datasources);
  const [resolvedAt, setResolvedAt] = React.useState<number>(0);

  React.useEffect(() => {
    let cancelled: boolean = false;
    if (inUse && compositeSiteId) {
      if (!peekClientType(clientListUrl, compositeSiteId)) {
        resolveClientType(clientListUrl, compositeSiteId)
          .then(() => { if (!cancelled) setResolvedAt(at => at + 1); })
          .catch(() => { /* resolveClientType never rejects; the guard is belt and braces */ });
      } else {
        // The cache may have been populated by ANOTHER consumer's flight landing between
        // this component's render (memo saw a miss -> inert) and this effect (peek hits).
        // Without this bump nothing ever re-runs the memo and the component stays inert
        // for its whole mount. When the render already saw the hit, the bump re-derives
        // the SAME context object (identity-stable), so downstream deps stay quiet.
        setResolvedAt(at => at + 1);
      }
    }
    return () => { cancelled = true; };
  }, [inUse, clientListUrl, compositeSiteId]);

  // Memoized so the returned context has a STABLE identity between resolutions - consumers put
  // it in useMemo/useEffect dependency arrays, and a fresh object per render would defeat them.
  // `resolvedAt` is the reactive stand-in for the module cache this memo reads.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useMemo(() => {
    if (!inUse || !compositeSiteId) {
      return INERT_CLIENT_TYPES;
    }
    return toClientTypeContext(peekClientType(clientListUrl, compositeSiteId));
  }, [inUse, clientListUrl, compositeSiteId, resolvedAt]);
};
